import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers, X } from "lucide-react";
import { createMapping, ELIGIBLE_PRODUCTS_QUERY_KEY, MAPPINGS_QUERY_KEY } from "@/services/channelCenter";
import { optionQueryKey, productOptionsApi } from "@/services/productOptions";
import { apiErrorText } from "@/pages/products/ProductOptionsTab";

/**
 * OPT-B2b · 上架中心「按选项连接」：产品有 ≥ 2 个选项时，勾平台要选连哪个选项（默认 = 默认选项）。
 * 入口：产品选项页「去上架中心连接」带 `?product_id=&option_id=` 进来直接定位；也可以从产品行的「按选项连接」打开（只带 product_id）。
 * 每个选项各自一套平台编号（选项编号；多日游 -R1…R4，加售 -X），各自推自己的价；停售的选项不能连。
 */
export function OptionConnectCard({ platformCode, platformName, productId, preOptionId, canManage, onClose }: {
  platformCode: string; platformName: string; productId: string; preOptionId?: string | null; canManage: boolean; onClose: () => void;
}) {
  const qc = useQueryClient();
  const optsQ = useQuery({ queryKey: optionQueryKey(productId), queryFn: () => productOptionsApi.list(productId) });
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const connect = useMutation({
    mutationFn: (optionId: string) => createMapping(platformCode, productId, optionId),
    onSuccess: (res) => {
      setMsg({ kind: "ok", text: (res.warnings ?? []).join("；") || `已连接到 ${platformName}` });
      qc.invalidateQueries({ queryKey: MAPPINGS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ELIGIBLE_PRODUCTS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: optionQueryKey(productId) });
    },
    onError: (e) => setMsg({ kind: "err", text: apiErrorText(e, "连接失败") }),
  });
  const options = optsQ.data ?? [];
  return (
    <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="option-connect-card">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Layers size={15} className="text-blue-600" />
        <b className="text-[14px] text-slate-900">按选项连接到 {platformName}</b>
        <span className="text-[12px] text-slate-500">每个选项各自一套平台编号、各推各的价</span>
        <button type="button" aria-label="关闭" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600"><X size={15} /></button>
      </div>
      {optsQ.isLoading ? <div className="py-3 text-[12.5px] text-slate-400">加载选项…</div> : optsQ.isError ? (
        <div className="py-3 text-[12.5px] text-rose-600">选项加载失败 · <button type="button" onClick={() => optsQ.refetch()} className="underline">重试</button></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[520px] text-[13px]" aria-label="选项连接">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><tr><th className="px-3 py-2 text-left">选项</th><th className="px-3 py-2 text-left">编号</th><th className="px-3 py-2 text-left">{platformName} 上的编号</th><th className="px-3 py-2 text-right">操作</th></tr></thead>
            <tbody>
              {options.map((o) => {
                const mine = o.mappings.filter((m) => m.platform_code === platformCode && m.status === "active" && m.option_kind !== "extra_vehicle");
                const inactive = o.status !== "active";
                return (
                  <tr key={o.id} className={`border-t border-slate-100 ${preOptionId === o.id ? "bg-blue-50" : ""}`} data-testid="connect-row" data-option-id={o.id}>
                    <td className="px-3 py-2"><span className="font-medium text-slate-800">{o.name_cn}</span>{o.is_default && <span className="ml-1.5 rounded-full bg-blue-50 px-1.5 text-[11px] font-semibold text-blue-700">默认</span>}{inactive && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-500">停售</span>}</td>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-500">{o.code}</td>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-600">{mine.length ? mine.map((m) => m.external_product_id).join("、") : <span className="text-slate-400">未连</span>}</td>
                    <td className="px-3 py-2 text-right">
                      {mine.length > 0
                        ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">已连</span>
                        : <button type="button" data-testid="connect-btn" disabled={!canManage || inactive || connect.isPending} title={inactive ? "停售的选项不能连平台" : !canManage ? "没有勾选权限" : undefined}
                            onClick={() => { setMsg(null); connect.mutate(o.id); }}
                            className="h-7 rounded-md bg-blue-600 px-2.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">连接</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {msg && <div role={msg.kind === "err" ? "alert" : "status"} data-testid="connect-msg" className={`mt-2 rounded-lg border px-3 py-2 text-[12.5px] ${msg.kind === "err" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{msg.text}</div>}
    </div>
  );
}
